// What each client has been invoiced — for one year, or across the whole
// history. Every figure comes from the same SQL aggregation the invoice list
// uses, so the numbers here and there always agree.

import { useState } from 'react';
import { api } from '../lib/api';
import { useAsync } from '../lib/hooks';
import { useI18n, useT } from '../lib/i18n';
import { useSession } from '../lib/session';
import { Icon } from '../components/Icon';
import { EmptyState, ErrorAlert, LoadingRow } from '../components/ui';
import { formatDate, formatMoney } from '@shared/format';

export function Reports() {
  const t = useT();
  const { locale } = useI18n();
  const { money } = useSession();

  // null means the entire period.
  const [year, setYear] = useState<number | null>(null);
  const [clientId, setClientId] = useState<number>();

  const report = useAsync(() => api.clientReport(year), [year]);
  const currency = (value: number) => formatMoney(value, money);

  if (clientId !== undefined) {
    return <ClientDetail clientId={clientId} onBack={() => setClientId(undefined)} />;
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">{t('reports.title')}</h1>
          <p className="page-head__subtitle">{t('reports.subtitle')}</p>
        </div>
        <div className="page-head__actions">
          <a className="btn" href={api.clientReportCsvUrl(year, locale)}>
            <Icon name="download" />{t('reports.download')}
          </a>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card__body">
          <div className="row">
            <span className="field__label" style={{ marginInlineEnd: 4 }}>{t('reports.period')}</span>
            <button type="button" className={`btn btn--sm${year === null ? ' btn--primary' : ''}`}
              onClick={() => setYear(null)}>
              {t('reports.allTime')}
            </button>
            {(report.data?.availableYears ?? []).map((available) => (
              <button key={available} type="button"
                className={`btn btn--sm${year === available ? ' btn--primary' : ''}`}
                onClick={() => setYear(available)}>
                {available}
              </button>
            ))}
          </div>
        </div>
      </div>

      {report.error ? <ErrorAlert message={report.error} onRetry={report.reload} />
        : report.loading && !report.data ? <LoadingRow />
        : report.data && report.data.rows.length === 0 ? (
          <div className="card">
            <EmptyState icon="file-text" title={t('reports.empty')} hint={t('reports.emptyHint')} />
          </div>
        ) : report.data && (
          <>
            <div className="stats">
              <div className="stat">
                <div className="stat__label">{t('reports.invoices')}</div>
                <div className="stat__value">{report.data.totals.invoiceCount}</div>
              </div>
              <div className="stat">
                <div className="stat__label">{t('reports.net')}</div>
                <div className="stat__value stat__value--muted">{currency(report.data.totals.subtotal)}</div>
              </div>
              <div className="stat">
                <div className="stat__label">{t('reports.tax')}</div>
                <div className="stat__value stat__value--muted">{currency(report.data.totals.taxTotal)}</div>
              </div>
              <div className="stat">
                <div className="stat__label">{t('reports.gross')}</div>
                <div className="stat__value">{currency(report.data.totals.total)}</div>
              </div>
              <div className="stat">
                <div className="stat__label">{t('reports.outstanding')}</div>
                <div className={`stat__value${report.data.totals.outstanding > 0 ? ' stat__value--warning' : ' stat__value--muted'}`}>
                  {currency(report.data.totals.outstanding)}
                </div>
              </div>
            </div>

            <div className="card">
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('reports.client')}</th>
                      <th className="shrink num">{t('reports.invoices')}</th>
                      <th className="num">{t('reports.net')}</th>
                      <th className="num">{t('reports.tax')}</th>
                      <th className="num">{t('reports.gross')}</th>
                      <th className="num">{t('reports.outstanding')}</th>
                      <th className="shrink">{t('reports.share')}</th>
                      <th className="shrink">{t('reports.last')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.data.rows.map((row) => {
                      const share = report.data!.totals.total > 0
                        ? (row.total / report.data!.totals.total) * 100 : 0;
                      return (
                        <tr key={row.clientId}>
                          <td>
                            <button type="button" className="table__primary linklike"
                              onClick={() => setClientId(row.clientId)}>
                              {row.clientName}
                            </button>
                            <div className="table__meta mono">{row.clientTaxId}</div>
                          </td>
                          <td className="shrink num">{row.invoiceCount}</td>
                          <td className="num">{currency(row.subtotal)}</td>
                          <td className="num table__meta">{currency(row.taxTotal)}</td>
                          <td className="num table__primary">{currency(row.total)}</td>
                          <td className="num">
                            {row.outstanding > 0
                              ? <span className="badge badge--pending">{currency(row.outstanding)}</span>
                              : <span className="subtle">—</span>}
                          </td>
                          <td className="shrink">
                            {/* A bar rather than a number: relative size is the
                                question being asked, and it reads at a glance. */}
                            <div className="share" title={`${share.toFixed(1)}%`}>
                              <div className="share__track">
                                <div className="share__fill" style={{ width: `${Math.max(share, 1)}%` }} />
                              </div>
                              <span className="share__label">{share.toFixed(1)}%</span>
                            </div>
                          </td>
                          <td className="shrink table__meta nowrap">
                            {row.lastInvoiceOn ? formatDate(row.lastInvoiceOn, locale) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="table__primary">{t('reports.totalsRow')}</td>
                      <td className="shrink num table__primary">{report.data.totals.invoiceCount}</td>
                      <td className="num table__primary">{currency(report.data.totals.subtotal)}</td>
                      <td className="num table__primary">{currency(report.data.totals.taxTotal)}</td>
                      <td className="num table__primary">{currency(report.data.totals.total)}</td>
                      <td className="num table__primary">{currency(report.data.totals.outstanding)}</td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </>
        )}
    </>
  );
}

/** One client, year by year. */
function ClientDetail({ clientId, onBack }: { clientId: number; onBack: () => void }) {
  const t = useT();
  const { locale } = useI18n();
  const { money } = useSession();
  const report = useAsync(() => api.clientDetailReport(clientId), [clientId]);

  const currency = (value: number) => formatMoney(value, money);

  if (report.loading && !report.data) return <LoadingRow />;
  if (report.error) return <ErrorAlert message={report.error} onRetry={report.reload} />;
  if (!report.data) return null;

  const { totals, years } = report.data;
  const peak = Math.max(...years.map((y) => y.total), 1);
  const average = totals.invoiceCount > 0 ? totals.total / totals.invoiceCount : 0;

  return (
    <>
      <div className="page-head">
        <div>
          <button type="button" className="btn btn--ghost btn--sm" onClick={onBack}>
            <Icon name="arrow-left" size={14} />{t('reports.backToAll')}
          </button>
          <h1 className="page-head__title" style={{ marginTop: 6 }}>
            {t('reports.clientHistory', { name: report.data.clientName })}
          </h1>
          <p className="page-head__subtitle mono">{report.data.clientTaxId}</p>
        </div>
      </div>

      <div className="stats">
        <div className="stat">
          <div className="stat__label">{t('reports.invoices')}</div>
          <div className="stat__value">{totals.invoiceCount}</div>
        </div>
        <div className="stat">
          <div className="stat__label">{t('reports.gross')}</div>
          <div className="stat__value">{currency(totals.total)}</div>
        </div>
        <div className="stat">
          <div className="stat__label">{t('reports.averageInvoice')}</div>
          <div className="stat__value stat__value--muted">{currency(average)}</div>
        </div>
        <div className="stat">
          <div className="stat__label">{t('reports.outstanding')}</div>
          <div className={`stat__value${totals.outstanding > 0 ? ' stat__value--warning' : ' stat__value--muted'}`}>
            {currency(totals.outstanding)}
          </div>
        </div>
        <div className="stat">
          <div className="stat__label">{t('reports.first')}</div>
          <div className="stat__value stat__value--muted" style={{ fontSize: 15 }}>
            {totals.firstInvoiceOn ? formatDate(totals.firstInvoiceOn, locale) : '—'}
          </div>
        </div>
      </div>

      <div className="card">
        {years.length === 0 ? (
          <EmptyState icon="file-text" title={t('reports.noYears')} />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th className="shrink">{t('reports.period')}</th>
                  <th className="shrink num">{t('reports.invoices')}</th>
                  <th className="num">{t('reports.net')}</th>
                  <th className="num">{t('reports.tax')}</th>
                  <th className="num">{t('reports.gross')}</th>
                  <th className="num">{t('reports.outstanding')}</th>
                  <th>{/* bar */}</th>
                </tr>
              </thead>
              <tbody>
                {years.map((row) => (
                  <tr key={row.year}>
                    <td className="shrink table__primary">{row.year}</td>
                    <td className="shrink num">{row.invoiceCount}</td>
                    <td className="num">{currency(row.subtotal)}</td>
                    <td className="num table__meta">{currency(row.taxTotal)}</td>
                    <td className="num table__primary">{currency(row.total)}</td>
                    <td className="num">
                      {row.outstanding > 0
                        ? <span className="badge badge--pending">{currency(row.outstanding)}</span>
                        : <span className="subtle">—</span>}
                    </td>
                    <td>
                      <div className="share share--wide">
                        <div className="share__track">
                          <div className="share__fill" style={{ width: `${(row.total / peak) * 100}%` }} />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="shrink table__primary">{t('reports.allTime')}</td>
                  <td className="shrink num table__primary">{totals.invoiceCount}</td>
                  <td className="num table__primary">{currency(totals.subtotal)}</td>
                  <td className="num table__primary">{currency(totals.taxTotal)}</td>
                  <td className="num table__primary">{currency(totals.total)}</td>
                  <td className="num table__primary">{currency(totals.outstanding)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
